import React from 'react';
import { observer } from 'mobx-react';

import { useTheme } from 'styled-components';
import { BaseText, LargeText } from '@ui-library/typography';
import { Flex } from '@ui-library/flex';
import { Tooltip } from '@ui-library/tooltip';
import { defaultFormat } from '@utils/dateUtils';

import { AuthTypeBadge } from './authTypeBadge';
import { AccountManagementStore } from '../store';
import { LinkedResourcesDropdownTag } from './linkedResourcesDropdownTag';
import { AccountOptions } from './accountOptions';
import { useDesignVariant } from '../designVariantContext';

interface AccountDetailsProps {
  store: AccountManagementStore;
  accountId: string | undefined;
}

export const AccountDetails: React.FC<AccountDetailsProps> = observer(({ store, accountId }) => {
  const { token } = useTheme();
  const destinationAccounts = store.compatibleAccounts;
  const accountDetails = destinationAccounts.find((account) => account.id === accountId);
  const accountDefinitionDetails = store.compatibleAccountDefinitions.find(
    (accountDef) => accountDef.name === accountDetails?.accountDefinitionName,
  );

  const isNext = useDesignVariant() === 'next';

  if (!accountDetails) {
    return (
      <BaseText
        fontWeight={isNext ? undefined : 600}
        color={isNext ? token.next.palette.grey800 : token.colorText}
      >
        No account is connected
      </BaseText>
    );
  }

  return (
    <Flex flexDirection="column" gap="4px" className="flex-1 overflow-hidden">
      <Flex flexDirection="row" gap="0.5rem" alignItemsCenter className="overflow-hidden">
        {isNext ? (
          <BaseText
            color={token.next.palette.grey800}
            className="text-ellipsis"
            title={accountDetails.name}
            fontWeight={500}
          >
            {accountDetails.name}
          </BaseText>
        ) : (
          <LargeText strong className="text-ellipsis" title={accountDetails.name}>
            {accountDetails.name}
          </LargeText>
        )}

        {accountDefinitionDetails && (
          <AuthTypeBadge
            label={accountDefinitionDetails.uiConfig.name}
            icon={accountDefinitionDetails.uiConfig.icon}
          />
        )}

        {accountId && (
          <LinkedResourcesDropdownTag accountId={accountId} resourceType={store.resourceType} />
        )}
      </Flex>

      <BaseText
        fontWeight={isNext ? undefined : 500}
        color={isNext ? token.next.palette.grey500 : token.colorTextTertiary}
      >
        {accountDetails.metadata?.userName ? `${accountDetails.metadata.userName} • ` : ''}
        <Tooltip
          title={`Created at: ${defaultFormat(accountDetails.createdAt)}`}
          placement="bottom"
        >
          {`Updated at: ${defaultFormat(accountDetails.updatedAt)}`}
        </Tooltip>
      </BaseText>

      {/* The option summary (provider/bucket/prefix/SSL) identifies a data-retention
          storage account. Source and destination accounts are identified by name +
          auth badge, so they keep their existing two-line details. */}
      {accountId && store.resourceType === 'dataRetention' && (
        <AccountOptions accountId={accountId} />
      )}
    </Flex>
  );
});
